import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faCheckCircle,
  faExclamationTriangle,
  faTruck,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import {
  OFFICE_LOCATIONS,
  DELIVERY_FEE_CONFIG,
  geocodeAddress,
  calculateHaversineDistance,
  calculateDeliveryFee
} from '../../services/distanceService';
import { useLocation as useServiceLocation } from '../../context/LocationContext';

/**
 * Front-door location gate for the rentals page. The customer must:
 *   1. Pick their area (which warehouse's catalog they want), and
 *   2. Enter their delivery ZIP so we can confirm — up front, before they
 *      ever browse — that they're inside our delivery radius for that location.
 * Only an in-range confirmation calls onConfirm(area), which unlocks browsing.
 */
const MAX = DELIVERY_FEE_CONFIG.maxDeliveryDistance;

const LocationGateModal = ({ locations, onConfirm }) => {
  // Ordering can be paused per location from the admin dashboard. Customers can
  // still pick the area and browse, but we say up front that we're not taking
  // new bookings there rather than letting them build a cart they can't submit.
  const { isOrderingPaused, getOrderingPauseMessage } = useServiceLocation();
  const [selectedArea, setSelectedArea] = useState(null); // 'Angola' | 'Elkhart'
  const [zip, setZip] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null); // { status, distance, otherInRange, otherArea, fee }
  const [error, setError] = useState('');

  const areaToOfficeKey = (area) => (area ? area.toLowerCase() : null);
  const officeKeyToArea = (key) => (key === 'angola' ? 'Angola' : 'Elkhart');

  const handleSelectArea = (area) => {
    setSelectedArea(area);
    setResult(null);
    setError('');
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!selectedArea) {
      setError('Please choose a location first.');
      return;
    }
    if (!/^\d{5}$/.test(zip.trim())) {
      setError('Please enter a valid 5-digit ZIP code.');
      return;
    }

    setChecking(true);
    try {
      const coords = await geocodeAddress(zip.trim());
      const selectedKey = areaToOfficeKey(selectedArea);
      const otherKey = selectedKey === 'angola' ? 'elkhart' : 'angola';

      const distSelected = calculateHaversineDistance(
        OFFICE_LOCATIONS[selectedKey].coordinates,
        coords
      );
      const distOther = calculateHaversineDistance(
        OFFICE_LOCATIONS[otherKey].coordinates,
        coords
      );

      const inRangeSelected = distSelected <= MAX;
      const inRangeOther = distOther <= MAX;

      if (inRangeSelected) {
        setResult({
          status: 'in-range',
          distance: Math.round(distSelected * 10) / 10,
          fee: calculateDeliveryFee(distSelected)
        });
      } else if (inRangeOther) {
        setResult({
          status: 'other-in-range',
          distance: Math.round(distSelected * 10) / 10,
          otherArea: officeKeyToArea(otherKey),
          otherDistance: Math.round(distOther * 10) / 10
        });
      } else {
        setResult({
          status: 'out-of-range',
          distance: Math.round(Math.min(distSelected, distOther) * 10) / 10
        });
      }
    } catch (err) {
      setError(
        "We couldn't verify that ZIP code. Please double-check it, or call us at (260) 226-5640."
      );
    } finally {
      setChecking(false);
    }
  };

  const selectedAddress = selectedArea ? locations[selectedArea]?.address : null;

  return (
    <div
      className="area-select-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="area-select-title"
    >
      <div className="area-select-modal">
        <FontAwesomeIcon icon={faMapMarkerAlt} className="area-select-icon" />
        <h2 id="area-select-title">Where are we delivering?</h2>
        <p>
          Each unit is stocked at one of our two locations, and we only deliver
          within <strong>{MAX} miles</strong> of that location. Pick your area and
          enter your ZIP so we can confirm we reach you{' '}
          <strong>before you start browsing</strong>.
        </p>

        {/* Step 1: pick the area */}
        <div className="area-select-options">
          {Object.keys(locations).map((loc) => (
            <button
              key={loc}
              type="button"
              className={`area-select-option ${selectedArea === loc ? 'selected' : ''} ${
                isOrderingPaused(loc) ? 'ordering-paused' : ''
              }`}
              onClick={() => handleSelectArea(loc)}
              aria-pressed={selectedArea === loc}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span className="area-select-name">{loc}</span>
              <span className="area-select-addr">{locations[loc].address}</span>
              {isOrderingPaused(loc) && (
                <span className="area-paused-badge">Not taking orders</span>
              )}
            </button>
          ))}
        </div>

        {/* Ordering paused for the area they picked — say so before the ZIP step */}
        {selectedArea && isOrderingPaused(selectedArea) && (
          <div className="area-result warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div>
              <strong>We're not taking new {selectedArea} orders right now.</strong>
              <span>{getOrderingPauseMessage(selectedArea)}</span>
            </div>
          </div>
        )}

        {/* Step 2: confirm ZIP is in range for the chosen area */}
        {selectedArea && (
          <form className="area-zip-check" onSubmit={handleCheck}>
            <label htmlFor="area-zip">
              <FontAwesomeIcon icon={faTruck} /> Confirm delivery to your ZIP
              (within {MAX} mi of our {selectedArea} location)
            </label>
            <div className="area-zip-row">
              <input
                id="area-zip"
                type="text"
                inputMode="numeric"
                maxLength={5}
                placeholder="ZIP code"
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value.replace(/\D/g, ''));
                  setResult(null);
                  setError('');
                }}
              />
              <button type="submit" className="area-zip-button" disabled={checking}>
                {checking ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Check'}
              </button>
            </div>
            {error && <div className="area-zip-error">{error}</div>}
          </form>
        )}

        {/* Result feedback */}
        {result?.status === 'in-range' && (
          <div className="area-result in-range">
            <FontAwesomeIcon icon={faCheckCircle} />
            <div>
              <strong>Great news — we deliver to you!</strong>
              <span>
                You're about {result.distance} mi from our {selectedArea} location
                {result.fee > 0 ? ` · delivery from $${result.fee.toFixed(2)}` : ''}.
              </span>
            </div>
            <button
              type="button"
              className="area-proceed-button"
              onClick={() => onConfirm(selectedArea)}
            >
              Browse {selectedArea} units
            </button>
          </div>
        )}

        {result?.status === 'other-in-range' && (
          <div className="area-result warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div>
              <strong>You're outside our {selectedArea} delivery range.</strong>
              <span>
                Your ZIP is about {result.distance} mi from {selectedArea} (limit{' '}
                {MAX} mi), but only {result.otherDistance} mi from our{' '}
                {result.otherArea} location — we can deliver those units to you.
              </span>
              {isOrderingPaused(result.otherArea) && (
                <span className="area-paused-note">
                  Heads up: we've paused new online orders for {result.otherArea}.{' '}
                  {getOrderingPauseMessage(result.otherArea)}
                </span>
              )}
            </div>
            <button
              type="button"
              className="area-proceed-button"
              onClick={() => onConfirm(result.otherArea)}
            >
              Browse {result.otherArea} units instead
            </button>
          </div>
        )}

        {result?.status === 'out-of-range' && (
          <div className="area-result out-of-range">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <div>
              <strong>Sorry — your area is outside our delivery range.</strong>
              <span>
                Your ZIP is about {result.distance} mi from our nearest location,
                and we currently deliver within {MAX} mi. Have questions or a
                special request? Call us at <strong>(260) 226-5640</strong>.
              </span>
            </div>
          </div>
        )}

        {selectedAddress && !result && (
          <p className="area-select-note">
            {selectedArea} units ship from {selectedAddress}.
          </p>
        )}
      </div>
    </div>
  );
};

export default LocationGateModal;
